import { useMemo } from 'react';
import { feedItems } from '@/mocks/home';
import TopicFilter from '@/pages/home/components/TopicFilter';

const tagClasses: Record<string, string> = {
  首发: 'bg-accent-100 text-accent-700',
  补充: 'bg-secondary-100 text-secondary-800',
  跟进: 'bg-primary-100 text-primary-700',
  反转: 'bg-foreground-950 text-background-50',
};

const followClasses: Record<string, string> = {
  跟进中: 'border-primary-200 bg-primary-50 text-primary-700',
  已反转: 'border-foreground-200 bg-background-200 text-foreground-800',
  待核实: 'border-secondary-200 bg-secondary-50 text-secondary-800',
};

type LiveFeedProps = {
  activeTopic: string;
  onChangeTopic: (topic: string) => void;
};

export default function LiveFeed({ activeTopic, onChangeTopic }: LiveFeedProps) {
  const items = useMemo(
    () => (activeTopic === '全部' ? feedItems : feedItems.filter((item) => item.topic === activeTopic)),
    [activeTopic],
  );

  return (
    <section id="feed" className="w-full border-b border-background-200 bg-background-100">
      <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-500 text-background-50 dark:text-foreground-950">
                <i className="ri-list-check-2" />
              </span>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">今日六点整发布 · 快讯流</h2>
            </div>
            <p className="mt-2 text-sm text-foreground-600">
              按时间线从新到旧排列，每条标注属于首发、补充、跟进，还是出现反转。
            </p>
          </div>
          <div className="flex items-center gap-2 font-label text-xs text-foreground-600">
            <i className="ri-sort-desc" />
            共 {items.length} 条 · 最近更新 18:00
          </div>
        </div>

        <div className="mt-5">
          <TopicFilter activeTopic={activeTopic} onChange={onChangeTopic} />
        </div>

        <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <ol className="relative flex flex-col">
              <span className="absolute left-[120px] top-2 hidden h-[calc(100%-16px)] w-px bg-background-300 sm:block" />
              {items.map((item, index) => (
                <li
                  key={item.id}
                  className="group relative animate-fade-up py-1"
                  style={{ animationDelay: `${index * 60}ms` }}
                >
                  <div className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start">
                    <div className="flex shrink-0 items-center gap-3 sm:w-[104px] sm:flex-col sm:items-end sm:gap-1">
                      <span className="font-label text-sm font-bold tabular-nums text-foreground-950">{item.time}</span>
                      <span className="font-label text-[10px] tracking-wider text-foreground-500">第 {index + 1} 条</span>
                    </div>
                    <span className="hidden h-2.5 w-2.5 shrink-0 translate-y-1.5 rounded-full border-2 border-background-100 bg-primary-500 ring-1 ring-background-300 sm:block" />
                    <article className="flex-1 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors group-hover:border-background-300">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`flex h-6 items-center rounded-md px-2 font-label text-[11px] font-medium ${
                            tagClasses[item.tag] ?? 'bg-background-200 text-foreground-700'
                          }`}
                        >
                          {item.tag}
                        </span>
                        <span className="flex h-6 items-center rounded-md bg-background-100 px-2 font-label text-[11px] text-foreground-600">
                          {item.topic}
                        </span>
                        <span
                          className={`flex h-6 items-center rounded-md border px-2 font-label text-[11px] ${
                            followClasses[item.follow] ?? 'border-background-200 bg-background-100 text-foreground-600'
                          }`}
                        >
                          {item.follow}
                        </span>
                        <span className="ml-auto flex items-center gap-1 font-label text-[11px] text-foreground-600">
                          <i className="ri-fire-line text-accent-500" />
                          {item.heat.toLocaleString()}
                        </span>
                      </div>
                      <h3 className="mt-2.5 font-heading text-base font-bold leading-snug text-foreground-950">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.summary}</p>
                    </article>
                  </div>
                </li>
              ))}
            </ol>
            {items.length === 0 && (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-8 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-inbox-line text-xl" />
                </span>
                <p className="mt-3 font-label text-sm text-foreground-600">该题材今日暂无收录，换个题材看看</p>
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-32 flex flex-col gap-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-price-tag-3-line text-primary-600" />
                  场次标记说明
                </h3>
                <ul className="mt-4 flex flex-col gap-3">
                  {[
                    { key: '首发', desc: '本条为当日首次出现，尚无官方说明' },
                    { key: '补充', desc: '在原有基础上追加的细节或素材' },
                    { key: '跟进', desc: '当事方或相关方已作出回应' },
                    { key: '反转', desc: '原有说法被新信息推翻，需重新判断' },
                  ].map((row) => (
                    <li key={row.key} className="flex items-start gap-3">
                      <span
                        className={`mt-0.5 flex h-6 shrink-0 items-center rounded-md px-2 font-label text-[11px] font-medium ${
                          tagClasses[row.key] ?? 'bg-background-200 text-foreground-700'
                        }`}
                      >
                        {row.key}
                      </span>
                      <span className="text-xs leading-relaxed text-foreground-600">{row.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-primary-200 bg-primary-50 p-4 md:p-5">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-shield-check-line text-primary-700" />
                  整理原则
                </h3>
                <p className="mt-3 text-xs leading-relaxed text-foreground-700">
                  未被多方交叉印证的说法一律标注为「待核实」；只有出现可核对的时间点、出处或当事方回应，才会升级为「已确认」。反转不会覆盖原文，会保留在时间线上。
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}